import bugsnagClient from '@/scripts/bugsnag'
import { DataError } from '@/utils/errors'
/*
  IMPORTANT:
  Make sure you are up to date with schema-form.md before making any changes
*/
import FormCheckbox from './FormCheckbox'
import FormText from './FormText'
import FormUrl from './FormUrl'
import FormExternalUrl from './FormExternalUrl'
import FormNumber from './FormNumber'
import FormMoney from './FormMoney'
import FormDate from './FormDate'
import FormEffectiveStartDate from './FormEffectiveStartDate'
import FormEffectiveEndDate from './FormEffectiveEndDate'
import FormTime from './FormTime'
import FormRange from './FormRange'
import FormTextarea from './FormTextarea'
import FormSelect from './FormSelect'
import FormFile from './FormFile'
import FormYesNo from './FormYesNo'
import FormCustomFontSelect from './FormCustomFontSelect'
import FormCourseNumber from './FormCourseNumber'
import FormCourseAliases from './FormCourseAliases'
import FormCourseSelect from './FormCourseSelect'
// eslint-disable-next-line no-restricted-imports
import FormProgramSelect from '@cm/client/components/Forms/FormProgramSelect.vue'
import FormDepartmentSelect from './FormDepartmentSelect'
import FormDepartmentOwnership from './FormDepartmentOwnership'
import FormCatalogSelect from './FormCatalogSelect'
import FormComponentSelect from './FormComponentSelect'
import FormOrganizationSelect from './specific/OrganizationSelect'
import FormUserSelect from './FormUserSelect'
import FormRoomSelect from './FormRoomSelect'
import FormTermSelect from './FormTermSelect'
import FormLearningOutcomes from './FormLearningOutcomes.vue'
import FormInstructionalMethods from './FormInstructionalMethods.vue'
import FormCredits from './FormCredits.vue'
import FormTopics from './FormTopics.vue'
import FormComponents from './FormComponents.vue'
import FormRequisites from './FormRequisites.vue'
import FormUploadImage from './FormUploadImage'
import FormSectionSelect from './FormSectionSelect'
import FormAttributeSelect from './FormAttributeSelect'
import FormCampusSelect from './FormCampusSelect'
import FormCampusMultiSelect from './FormCampusMultiSelect'
import FormGradeModeSelect from './FormGradeModeSelect'
import FormGradeModesMultiSelect from './FormGradeModesMultiSelect'
import FormLocationSelect from './FormLocationSelect'
import FormRequirementGroupSelect from './FormRequirementGroupSelect'
import FormCourseGroupSelect from './FormCourseGroupSelect'
import FormWysiwyg from './FormWysiwyg'
import FormSignature from './FormSignature'
import FormColor from './FormColor'
import FormTags from './FormTags'
// eslint-disable-next-line no-restricted-imports
import SectionsFilter from '@sm/client/components/InstitutionSettings/Buildings/SectionsFilter'
import FormFieldOfStudySelect from './FormFieldOfStudySelect'
import FormDegreeSelect from './FormDegreeSelect'
import FormCipCodeSelect from './FormCipCodeSelect'
import FormHegisCodeSelect from './FormHegisCodeSelect'
import FormCollegeSelect from './FormCollegeSelect'
import FormCareerSelect from './FormCareerSelect'
import FormProgramLevelSelect from './FormProgramLevelSelect'
import FormRequirementDesignationSelect from './FormRequirementDesignationSelect'
import FormMilestoneSelect from './FormMilestoneSelect'
import FormWeekdayPicker from './FormWeekdayPicker'
import FormOwnersSelect from './FormOwnersSelect'
import FormProgramSpecializations from './FormProgramSpecializations'
import FormHeading from './FormHeading'
import FormDerived from './FormDerived'
import FormContactsList from './FormContactsList'
import FormCareerPathsList from './FormCareerPathsList'
import FormObjectMergeSettings from './FormObjectMergeSettings'
import FormSemesterSelect from './FormSemesterSelect'
import FormYearSelect from './FormYearSelect'
import FormPhaseSelect from './FormPhaseSelect'
import FormDynamicFilters from './FormDynamicFilters'
import FormReferenceValue from './FormReferenceValue.vue'
// eslint-disable-next-line no-restricted-imports
import FormSySectionSelect from '@sy/client/components/Forms/FormSySectionSelect'
// eslint-disable-next-line no-restricted-imports
import FormSySectionInstructors from '@sy/client/components/Forms/FormSyInstructors'
// eslint-disable-next-line no-restricted-imports
import FormSySectionMeetingPatternsAndRooms from '@sy/client/components/Forms/FormSyMeetingPatternsAndRooms'
// The following two fields are specific to the Peoplesoft SIS System
import FormTuitionGroupSelect from './FormTuitionGroupSelect'
import FormNscClassificationSelect from './FormNscClassificationSelect'
import FormSulaSpecialProgramSelect from './FormSulaSpecialProgramSelect'

import AppLoading from '@/components/App/AppLoading'

// FormPartsOfTerm is loaded async to avoid recursive loop
// issue due to nested schema forms being in this component
const FormPartsOfTerm = () => import('./FormPartsOfTerm')

const DegreeMapAsync = () => ({
  component: import('./FormDegreeMaps/FormDegreeMaps'),
  loading: AppLoading,
})

export default function matchInputTypes (field) {
  if (!!field.config?.referenceValue?.referenceSource && !!field.config?.referenceValue?.referenceKey) {
    return {
      ...field,
      type: FormReferenceValue,
    }
  }
  if (field.specialType) {
    // BELOW line added for backwards compatibility of field options for section modal.
    if (typeof field.specialType === 'string') field.inputType = field.specialType
    return {
      ...field,
      type: typeof field.inputType === 'string'
        ? () => import(`./specific/${field.inputType}.js`)
        : field.inputType,
      // TODO: FIXME: Due to function caching is not possible
      // which leads to reloading component on each property update.
      disabled: field.locked,
      // TODO: Once we're sure `selectOptions` is no longer present on any db data, we can remove that
      options: field.selectOptions || (field.config && field.config.options) || [],
    }
  }
  if (field.component) {
    return {
      ...field,
      type: field.component,
    }
  }

  if (field.type) return field
  const inputType = field.inputType || field.questionType
  /* These could be just component refs at some point, instead of strings,
   * and we could get rid of these components being registered locally,
   * but the main issue is with how SchemaForm works with `layout` property:
   *
   * Becuase `layout` and `value` are passed by the same computed properties,
   * they both trigger re-render of whole forms, which would cause components like
   * type: import('./FormText')
   * to re-render with every @input, so it would loose focus
   * (because destroyed component emits 'blur').
   */
  switch (inputType) {
    case 'checkbox':
      return {
        ...field,
        inputType,
        type: FormCheckbox,
        disabled: field.locked,
      }
    case 'select':
      return {
        ...field,
        inputType,
        type: FormSelect,
        disabled: field.locked,
        config: {
          multiple: false,
          // TODO: Once we're sure `selectOptions` is no longer present on any db data, we can remove that
          options: field.selectOptions || (field.config && field.config.options) || [],
          ...field.config,
        },
      }
    // TODO: This case should be deleted once we're sure no db data has it (replace with just select /w proper config)
    case 'multipleSelect':
      return {
        ...field,
        inputType,
        type: FormSelect,
        disabled: field.locked,
        config: {
          multiple: true,
          // TODO: Once we're sure `selectOptions` is no longer present on any db data, we can remove that
          options: field.selectOptions || (field.config && field.config.options) || [],
          ...field.config,
        },
      }
    case 'text':
      return {
        ...field,
        inputType,
        type: FormText,
        disabled: field.locked,
      }
    case 'url':
      return {
        ...field,
        inputType,
        type: FormUrl,
        disabled: field.locked,
      }
    case 'externalUrl':
      return {
        ...field,
        inputType,
        type: FormExternalUrl,
        disabled: field.locked,
      }
    case 'boolean':
      return {
        ...field,
        inputType,
        type: FormText,
        disabled: field.locked,
      }
    case 'link':
      return {
        ...field,
        inputType,
        type: FormText,
        disabled: field.locked,
        config: { type: 'url' },
      }
    case 'textarea':
      return {
        ...field,
        inputType,
        type: FormTextarea,
        disabled: field.locked,
      }
    case 'number':
      return {
        ...field,
        inputType,
        type: FormNumber,
        disabled: field.locked,
        config: {
          // There might be a case somewhere in the app,
          // that `field` does have any of these directly on itself,
          // instead of on `config`, so let's leave these fallbacks for now.
          ...(field.step && { step: field.step }),
          ...(typeof field.min === 'number' && { min: field.min }),
          ...(typeof field.max === 'number' && { max: field.max }),
          ...(field.config || {}),
        },
      }
    case 'wysiwyg':
      return {
        ...field,
        inputType,
        type: FormWysiwyg,
        disabled: field.locked,
      }
    case 'money':
      return {
        ...field,
        inputType,
        type: FormMoney,
        disabled: field.locked,
      }
    case 'date':
      return {
        ...field,
        inputType,
        type: FormDate,
        disabled: field.locked,
      }
    case 'referenceDate':
      return {
        ...field,
        type: () => import('./FormReferenceDate.vue'),
      }
    case 'time':
      return {
        ...field,
        inputType,
        type: FormTime,
        disabled: field.locked,
      }
    case 'file':
      return {
        ...field,
        inputType,
        type: FormFile,
        disabled: field.locked,
      }
    case 'yesNo':
      return {
        ...field,
        inputType,
        type: FormYesNo,
        disabled: field.locked,
      }
    case 'customFontSelect':
      return {
        ...field,
        inputType,
        type: FormCustomFontSelect,
        disabled: field.locked,
      }
    case 'courseSelect':
      return {
        ...field,
        inputType,
        type: FormCourseSelect,
        disabled: field.locked,
        config: {
          trackByGroupId: true,
          ...field.config,
        },
      }
    case 'crossListedCourseSelect':
      return {
        ...field,
        inputType,
        type: () => import('./FormCrossListedCourseSelect.vue'),
        disabled: field.locked,
      }
    case 'courseEquivalenciesSelect':
      return {
        ...field,
        inputType,
        type: () => import('./FormCourseEquivalenciesSelect.vue'),
        disabled: field.locked,
      }
    case 'crossListedCourseRead':
      return {
        ...field,
        inputType,
        type: () => import('./FormCrossListedCourseRead.vue'),
        disabled: true,
      }
    case 'programSelect':
      return {
        ...field,
        inputType,
        type: FormProgramSelect,
        disabled: field.locked,
      }
    case 'organizationSelect':
    case 'organization':
      return {
        ...field,
        inputType,
        type: FormOrganizationSelect,
        disabled: field.locked,
      }
    case 'departmentSelect':
      return {
        ...field,
        inputType,
        type: FormDepartmentSelect,
        disabled: field.locked,
      }
    case 'departmentOwnership':
      return {
        ...field,
        inputType,
        type: FormDepartmentOwnership,
        disabled: field.locked,
      }
    case 'courseNumber':
      return {
        ...field,
        inputType,
        type: FormCourseNumber,
        disabled: field.locked,
      }
    case 'courseAliases':
      return {
        ...field,
        inputType,
        type: FormCourseAliases,
        disabled: field.locked,
      }
    case 'subjectCodeSelect':
      return {
        ...field,
        inputType,
        type: () => import(`./Variations/${inputType}.js`),
        disabled: field.locked,
      }
    case 'userSelect':
      return {
        ...field,
        inputType,
        type: FormUserSelect,
        disabled: field.locked,
      }
    case 'roomSelect':
      return {
        ...field,
        inputType,
        type: FormRoomSelect,
        disabled: field.locked,
      }
    case 'owners':
      return {
        ...field,
        inputType,
        type: FormUserSelect,
        disabled: field.locked,
      }
    case 'termSelect':
      return {
        ...field,
        inputType,
        type: FormTermSelect,
        disabled: field.locked,
      }
    case 'learningOutcomes':
      return {
        ...field,
        inputType,
        type: FormLearningOutcomes,
        disabled: field.locked,
      }
    case 'instructionalMethods':
      return {
        ...field,
        inputType,
        type: FormInstructionalMethods,
        disabled: field.locked,
      }
    case 'range':
      return {
        ...field,
        inputType,
        type: FormRange,
        disabled: field.locked,
      }
    case 'credits':
      return {
        ...field,
        inputType,
        type: FormCredits,
        disabled: field.locked,
      }
    case 'topics':
      return {
        ...field,
        inputType,
        type: FormTopics,
        disabled: field.locked,
      }
    case 'componentSelect':
      return {
        ...field,
        type: FormComponentSelect,
        disabled: field.locked,
      }
    case 'components':
      return {
        ...field,
        inputType,
        type: FormComponents,
        disabled: field.locked,
      }
    case 'requisites':
      return {
        ...field,
        inputType,
        type: FormRequisites,
        disabled: field.locked,
      }
    case 'uploadImage':
      return {
        ...field,
        inputType,
        type: FormUploadImage,
        disabled: field.locked,
      }
    // `roomTemplateQuestions`
    case 'sectionsFilter':
      return {
        ...field,
        inputType,
        type: SectionsFilter,
        disabled: field.locked,
      }
    case 'sectionSelect':
      return {
        ...field,
        inputType,
        type: FormSectionSelect,
        disabled: field.locked,
      }
    case 'syllabusSectionSelect':
      return {
        ...field,
        inputType,
        type: FormSySectionSelect,
        disabled: field.locked,
      }
    case 'syllabusSectionInstructors':
      return {
        ...field,
        inputType,
        type: FormSySectionInstructors,
        disabled: field.locked,
      }
    case 'syllabusSectionMeetingPatternsAndRooms':
      return {
        ...field,
        inputType,
        type: FormSySectionMeetingPatternsAndRooms,
        disabled: field.locked,
      }
    case 'attributeSelect':
      return {
        ...field,
        inputType,
        type: FormAttributeSelect,
        disabled: field.locked,
      }
    case 'fieldOfStudySelect':
      return {
        ...field,
        inputType,
        type: FormFieldOfStudySelect,
        disabled: field.locked,
      }
    case 'campusSelect':
      return {
        ...field,
        inputType,
        type: FormCampusSelect,
        disabled: field.locked,
      }
    case 'campusMultiSelect':
      return {
        ...field,
        inputType,
        type: FormCampusMultiSelect,
        disabled: field.locked,
      }
    case 'gradeModeSelect':
      return {
        ...field,
        type: FormGradeModeSelect,
        disabled: field.locked,
      }
    case 'gradeModesMultiSelect':
      return {
        ...field,
        type: FormGradeModesMultiSelect,
        disabled: field.locked,
      }
    case 'locationSelect':
      return {
        ...field,
        inputType,
        type: FormLocationSelect,
        disabled: field.locked,
      }
    case 'cipCodeSelect':
      return {
        ...field,
        inputType,
        type: FormCipCodeSelect,
        disabled: field.locked,
      }
    case 'degreeSelect':
      return {
        ...field,
        inputType,
        type: FormDegreeSelect,
        disabled: field.locked,
      }
    case 'hegisCodeSelect':
      return {
        ...field,
        inputType,
        type: FormHegisCodeSelect,
        disabled: field.locked,
      }
    case 'collegeSelect':
      return {
        ...field,
        inputType,
        type: FormCollegeSelect,
        disabled: field.locked,
      }
    case 'careerSelect':
      return {
        ...field,
        inputType,
        type: FormCareerSelect,
        disabled: field.locked,
      }
    case 'programLevelSelect':
      return {
        ...field,
        inputType,
        type: FormProgramLevelSelect,
        disabled: field.locked,
      }
    case 'requirementGroupSelect':
      return {
        ...field,
        inputType,
        type: FormRequirementGroupSelect,
        disabled: field.locked,
      }
    case 'courseGroupSelect':
      return {
        ...field,
        inputType,
        type: FormCourseGroupSelect,
        disabled: field.locked,
      }
    case 'requirementDesignationSelect':
      return {
        ...field,
        inputType,
        type: FormRequirementDesignationSelect,
        disabled: field.locked,
      }
    case 'milestoneSelect':
      return {
        ...field,
        inputType,
        type: FormMilestoneSelect,
        disabled: field.locked,
      }
    case 'signature':
      return {
        ...field,
        type: FormSignature,
        disabled: field.locked,
      }
    case 'color':
      return {
        ...field,
        type: FormColor,
        disabled: field.locked,
      }
    case 'tags':
      return {
        ...field,
        type: FormTags,
        disabled: field.locked,
      }
    case 'weekdays':
      return {
        ...field,
        type: FormWeekdayPicker,
        disabled: field.locked,
      }
    case 'ownersSelect':
      return {
        ...field,
        type: FormOwnersSelect,
        disabled: field.locked,
      }
    case 'degreeMaps':
      return {
        ...field,
        inputType,
        type: DegreeMapAsync,
        disabled: field.locked,
      }
    case 'specializations':
      return {
        ...field,
        type: FormProgramSpecializations,
        disabled: field.locked,
      }
    case 'nscClassificationSelect':
      return {
        ...field,
        inputType,
        type: FormNscClassificationSelect,
        disabled: field.locked,
      }
    case 'sulaSpecialProgramSelect':
      return {
        ...field,
        inputType,
        type: FormSulaSpecialProgramSelect,
        disabled: field.locked,
      }
    case 'tuitionGroupSelect':
      return {
        ...field,
        inputType,
        type: FormTuitionGroupSelect,
        disabled: field.locked,
      }
    case 'effectiveEndDate':
      return {
        ...field,
        inputType,
        type: FormEffectiveEndDate,
        disabled: field.locked,
      }
    case 'effectiveStartDate':
      return {
        ...field,
        inputType,
        type: FormEffectiveStartDate,
        disabled: field.locked,
      }
    case 'heading':
      return {
        ...field,
        inputType,
        type: FormHeading,
        disabled: field.locked,
      }
    case 'derived':
      return {
        ...field,
        inputType,
        type: FormDerived,
        disabled: field.locked,
      }
    case 'contactsList':
      return {
        ...field,
        inputType,
        type: FormContactsList,
        disabled: field.locked,
      }
    case 'careerPathsList':
      return {
        ...field,
        inputType,
        type: FormCareerPathsList,
        disabled: field.locked,
      }
    case 'objectMergeSettings':
      return {
        ...field,
        inputType,
        type: FormObjectMergeSettings,
        disabled: field.locked,
      }
    case 'dependencies':
      // TODO: dependencies is a hardcoded field and needs a placeholder component to display in Form Preview
      return {
      }
    case 'courseSchedule':
      // TODO: courseSchedule is a hardcoded field and needs a placeholder component to display in Form Preview
      return {
      }
    case 'catalogSelect':
      return {
        ...field,
        inputType,
        type: FormCatalogSelect,
        disabled: field.locked,
      }
    case 'sessionIndicatorSelect':
      return {
        ...field,
        type: () => import('@/components/Forms/FormSessionIndicatorSelect'),
      }
    case 'employeeRecordSelect':
      return {
        ...field,
        type: () => import('@/components/Forms/FormEmployeeRecordSelect'),
      }
    // Scheduling - Section Editor (SectionForm) components
    case 'times':
      return {
        ...field,
        type: () => import('@sm/client/components/SchedulingManagement/sections/subdomains/meetingPatterns/MeetingPatternsAndRooms'),
      }
    case 'professors':
      return {
        ...field,
        type: () => import('@sm/client/components/SchedulingManagement/sections/subdomains/instructorsMeta/Instructors'),
      }
    case 'relationships':
      return {
        ...field,
        type: () => import('@sm/client/components/CourseEditor/NEW_Relationships'),
      }
    case 'notesList':
      return {
        ...field,
        type: () => import('@sm/client/components/SchedulingManagement/sections/subdomains/notes/NotesList'),
      }
    case 'noteNbr':
      return {
        ...field,
        type: () => import('@/components/Forms/FormNoteNbr'),
      }
    case 'reserveCapacity':
      return {
        ...field,
        type: () => import('@sm/client/components/SchedulingManagement/sections/subdomains/reserveCapacity/ReserveCapacity'),
      }
    case 'courseMaterials':
      return {
        ...field,
        type: () => import('@sm/client/components/SchedulingManagement/sections/subdomains/courseMaterials/CourseMaterials'),
      }
    case 'examMeetings':
      return {
        ...field,
        type: () => import('@sm/client/components/SchedulingManagement/sections/subdomains/examMeetings/ExamMeetings'),
      }
    case 'otherArCodes':
      return {
        ...field,
        type: () => import('@sm/client/components/SchedulingManagement/sections/subdomains/otherArCodes/OtherArCodes'),
      }
      // term template specific fields
    case 'semesterSelect':
      return {
        ...field,
        inputType,
        type: FormSemesterSelect,
      }
    case 'yearSelect':
      return {
        ...field,
        inputType,
        type: FormYearSelect,
      }
    case 'phaseSelect':
      return {
        ...field,
        inputType,
        type: FormPhaseSelect,
      }
    case 'partsOfTerm':
      return {
        ...field,
        inputType,
        type: FormPartsOfTerm,
      }
    case 'dynamicFilters':
      return {
        ...field,
        inputType,
        type: FormDynamicFilters,
      }
    case 'questionWithNestedFields':
      return {
        ...field,
        type: () => import('@/components/Forms/FormQuestionWithNestedFields/FormQuestionWithNestedFields'),
      }
    default:
      bugsnagClient.notify(
        new DataError(
          `Unknown \`inputType\` (${field.inputType}) ` +
          `or \`questionType\` (${field.questionType})`
        ),
        event => event.addMetadata('field', field),
      )
      return {}
  }
}
